import { getAuthHeaders } from './auth.js'
import { groceryLookupKey } from './ingredientQuantity.js'

function uniqueLookupNames(items) {
  const names = []
  const seen = new Set()
  ;(Array.isArray(items) ? items : []).forEach((item) => {
    const raw = item?.item || item?.name
    if (!raw) return
    const lookup = groceryLookupKey(raw) || String(raw).trim().toLowerCase()
    if (!lookup || seen.has(lookup)) return
    seen.add(lookup)
    names.push(lookup)
  })
  return names
}

function isUsableCategory(category) {
  if (category == null) return false
  const n = String(category).trim().toLowerCase()
  return Boolean(n) && n !== 'unknown' && n !== 'uncategorized'
}

/**
 * Batch lookup returns rows in the same order as the query list, but `item` is the
 * DB product name (e.g. "Salmon Fillet") — not the query ("salmon fillets").
 * Index by query string so qty-stripped cart lines can merge metadata.
 */
function indexLookupResultsByQuery(lookupNames, results) {
  const byKey = new Map()
  const rows = Array.isArray(results) ? results : []
  lookupNames.forEach((lookupName, i) => {
    const r = rows[i]
    if (!r || !lookupName) return
    byKey.set(lookupName, r)
    const returned = groceryLookupKey(r.item || r.name || '')
    if (returned && !byKey.has(returned)) byKey.set(returned, r)
  })
  return byKey
}

/**
 * Re-key batch lookup rows to the query strings used, so REFRESH_METADATA can
 * match qty-stripped cart lines even when the API returns a different DB name.
 */
export function remapBatchResultsToLookupQueries(lookupNames, results) {
  const rows = Array.isArray(results) ? results : []
  return (Array.isArray(lookupNames) ? lookupNames : [])
    .map((lookupName, i) => {
      const r = rows[i]
      if (!r || !lookupName) return null
      return { ...r, item: lookupName }
    })
    .filter(Boolean)
}

function applyLookupMeta(item, result) {
  if (!result) return item
  const next = { ...item }
  if (isUsableCategory(result.category)) {
    next.category = result.category
  }
  if (result.aisle_id != null && result.aisle_id !== 'Unknown' && result.aisle_id !== 'Error') {
    next.aisle_id = result.aisle_id
  }
  if (result.aisle_name != null && result.aisle_name !== 'Not Found') {
    next.aisle_name = result.aisle_name
  }
  if (result.confidence !== undefined) {
    next.confidence = result.confidence
  }
  return next
}

/**
 * Attach category / aisle metadata before items are shown in Shop.
 * Looks up qty-stripped product names so "4 salmon fillets" matches via query order
 * even when the API returns a singular DB name like "Salmon Fillet".
 *
 * @param {Array<object>} items
 * @param {{ storeId?: string | null, fetchImpl?: typeof fetch }} [options]
 * @returns {Promise<object[]>}
 */
export async function enrichGroceryItemsWithLookup(items, { storeId = null, fetchImpl = fetch } = {}) {
  const list = Array.isArray(items) ? items : []
  if (!list.length || !storeId) return list

  const itemNames = uniqueLookupNames(list)
  if (!itemNames.length) return list

  try {
    const params = new URLSearchParams()
    params.set('store', String(storeId))
    const response = await fetchImpl(`/grocery/lookup/batch?${params}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ items: itemNames }),
    })
    if (!response.ok) return list

    const data = await response.json()
    const byKey = indexLookupResultsByQuery(itemNames, data?.results)
    if (byKey.size === 0) return list

    return list.map((it) => {
      const exact = (it?.item || it?.name || '').toString().trim().toLowerCase()
      const stripped = groceryLookupKey(exact)
      const r = (stripped && byKey.get(stripped)) || (exact && byKey.get(exact)) || null
      return applyLookupMeta(it, r)
    })
  } catch {
    return list
  }
}

export { uniqueLookupNames, indexLookupResultsByQuery, applyLookupMeta }
